import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { Box, type BoxProps, Loading } from '@enechain/ecloud-designsystem'

/**
 * 領域のローディング (全画面共通。UI 辞書 `design/ui-feasibility/components/region-loading.md`、
 * 2026-10-15 デザイナー指示「対象箇所の全表示に時間がかかる場合 (0.3s 以上) は shadow + spinner」)。
 *
 * - 表示を始めてから {@link REGION_LOADING_DELAY_MS} 以内に終われば何も出さない。過ぎたらその時点から
 *   終わるまで出す。最短の表示時間は設けない (速い時に点滅させない・遅い時だけ待っていると分かる)
 * - 初回の表示と、フィルタ・期間・タブ・ページ送り・並べ替えの後の表示し直しの両方が対象。
 *   表示し直しは `isFetching` を渡す (`isPending` だけだと前の内容を残している間は出ない)
 * - 見た目: 対象の領域だけを影で覆い、中央に DS の `Loading`。前の内容は影の下に残す。
 *   初回で中身がまだ無い時は影でなく面の色で覆う (2026-10-27 デザイナー決定)
 * - 1 つの操作で複数の領域が変わる時は共通の親を 1 枚で覆う。ツールバー・ページタイトル・
 *   サイドメニュー・画面全体は覆わない
 * - 取得中は子の領域を inert にし、影が出る前もクリック・キーボード操作を止める。器に `aria-busy`
 *
 * 使わない場面: 保存・送信 (ボタンの loading)、取り込み・計算など長いと分かっている処理 (各画面の進捗表示)、
 * 行内の小さな値の読み込み、アプリを開いた直後の全画面 (`ProtectedAuthGuard` の `LoadingEnechainLogo`)。
 *
 * 読み込み中は領域の中に空状態の文言を出さない (呼び出し側で「データが無い」と「まだ来ていない」を分ける)。
 * 失敗したら `isLoading` を false にして、領域の中に取得の失敗を出す。
 */
export const REGION_LOADING_DELAY_MS = 300

/** 中身がまだ無い時に、スピナーが見える高さを保つ (表の空・読み込み中の行と同じ 88px)。 */
export const REGION_LOADING_MIN_HEIGHT = '88px'

/**
 * `isLoading` が {@link REGION_LOADING_DELAY_MS} 続いた時だけ true を返す。false になったら即座に false。
 */
export function useDelayedLoading(
  isLoading: boolean,
  delayMs: number = REGION_LOADING_DELAY_MS,
): boolean {
  const [isElapsed, setElapsed] = useState(false)
  useEffect(() => {
    if (!isLoading) {
      setElapsed(false)
      return undefined
    }
    const timer = setTimeout(() => setElapsed(true), delayMs)
    return () => clearTimeout(timer)
  }, [isLoading, delayMs])
  return isLoading && isElapsed
}

type OverlayProps = {
  /**
   * 初回で中身がまだ無いか。true なら影と blur を付けず `Surface.primary` で覆う
   * (下に古い内容が無いので、影で示すものが無い)。
   */
  isEmpty?: boolean
}

/**
 * 領域を覆う影 + 中央のスピナー。親に `position="relative"` が要る ([[RegionLoading]] が持つ)。
 *
 * [DS差異] DS の `Loading` には影が無い。影は「下の内容が古い (取り直している)」ことを示すために足す。
 * 値は 8080 `demand/forecast-today.html` の `.dft-spinner` (`rgba(0,0,0,0.18)` + `blur(1px)`)。
 */
export const RegionLoadingOverlay = ({
  isEmpty = false,
}: OverlayProps): React.ReactElement => (
  <Box
    position="absolute"
    zIndex={10}
    backgroundColor={isEmpty ? 'Surface.primary' : 'rgba(0, 0, 0, 0.18)'}
    css={isEmpty ? undefined : { backdropFilter: 'blur(1px)' }}
    data-testid="region-loading-overlay"
    inset={0}
  >
    <Loading />
  </Box>
)

/**
 * 覆いを除く中身に、高さを持って描かれた要素が 1 つでもあるか。
 * flex-grow で器の高さまで伸ばされた子 (`PageScrollArea` 等) は空でも高さを持つので、その子の中身で判定する。
 * `display: contents` の子は自身の箱を持たないので、その子の中身で判定する。
 */
const hasRenderedContent = (parent: Element): boolean =>
  Array.from(parent.children).some((child) => {
    const style = getComputedStyle(child)
    if (style.display === 'contents') return hasRenderedContent(child)
    if (child.getBoundingClientRect().height <= 0) return false
    return Number.parseFloat(style.flexGrow) > 0
      ? hasRenderedContent(child)
      : true
  })

type Props = BoxProps & {
  /** 取得中か (初回の `isPending` と、表示し直しの `isFetching`)。 */
  isLoading: boolean
  children?: ReactNode
}

/**
 * 領域の器。読み込みが {@link REGION_LOADING_DELAY_MS} を超えたら [[RegionLoadingOverlay]] で覆う。
 * 中身がまだ無い間も影とスピナーが見えるよう、読み込み中は {@link REGION_LOADING_MIN_HEIGHT} の高さを保つ。
 */
export const RegionLoading = ({
  isLoading,
  children,
  ...boxProps
}: Props): React.ReactElement => {
  const isShown = useDelayedLoading(isLoading)
  const contentRef = useRef<HTMLDivElement>(null)
  const [isEmpty, setEmpty] = useState(false)
  // 影を出している間は中身 (children) の描き直しのたびに測り、中身が描かれたら影に戻す。
  // 塗る前に切り替えてちらつかせない
  useLayoutEffect(() => {
    const content = contentRef.current
    if (!isShown || content === null) return
    const nextIsEmpty = !hasRenderedContent(content)
    if (nextIsEmpty !== isEmpty) setEmpty(nextIsEmpty)
  }, [isShown, isEmpty, children])
  return (
    <Box
      position="relative"
      minHeight={isLoading ? REGION_LOADING_MIN_HEIGHT : undefined}
      aria-busy={isLoading}
      {...boxProps}
    >
      {/* 影の300ms遅延とは独立に、旧データのクリック・focus・Enterを止める。
          display:contentsで親のflex/grid配置を保ち、スピナーはinertの外に置く。
          React 18の型にはinertが無いため、HTMLの空文字属性として渡す。 */}
      <Box
        ref={contentRef}
        display="contents"
        {...(isLoading ? { inert: '' } : {})}
      >
        {children}
      </Box>
      {isShown && <RegionLoadingOverlay isEmpty={isEmpty} />}
    </Box>
  )
}
